import { Avatar, Box, Text, TooltipCommonProps } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { AIFilled } from '@wix/wix-ui-icons-common';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { CaroIcon } from '../../../common/components/CaroIcon';
import { useIsAiChatbot } from '../../../common/hooks/useIsAiChatbot';
import { selectIsConnectingAgent, selectIsLiveChat } from '../../../common/selectors/chat';
import { selectOnlineAgent } from '../../../common/selectors/header';
import { AnnouncementBanner } from '../../../components/header/AnnouncementBanner';
import { StatusBanner } from '../../../components/header/StatusBanner';
import { headerFullPageDataHooks } from './HeaderFullPage.dataHooks';
import { HeaderFullPageActions } from './HeaderFullPageActions';
import style from './styles.scss';

const titleTooltipProps: TooltipCommonProps = {
  size: 'small',
  placement: 'bottom',
};

export const HeaderFullPage: FC = () => {
  const { t } = useTranslation();
  const isLiveChat = useSelector(selectIsLiveChat);
  const agent = useSelector(selectOnlineAgent);
  const isAiChatbot = useIsAiChatbot();
  const isConnectingAgent = useSelector(selectIsConnectingAgent);

  const titleSection = useMemo(() => {
    const shouldShowAgentInfo = isLiveChat && agent;

    if (isAiChatbot) {
      return (
        <Box direction="vertical" gap="0.5">
          <Text size="tiny" weight="thin" light dataHook={headerFullPageDataHooks.title()}>
            {t<TLocalesKeys>('subheader_title')}
          </Text>
          {shouldShowAgentInfo ? (
            <Box gap="0.5" verticalAlign="middle">
              <Avatar
                size="size24"
                shape="square"
                name={agent.fullName}
                imgProps={{ src: agent.profileImage }}
                dataHook={headerFullPageDataHooks.agentAvatar()}
              />

              <Text light weight="bold" ellipsis tooltipProps={titleTooltipProps}>
                {agent.fullName}
              </Text>
            </Box>
          ) : (
            <Box gap="0.5" verticalAlign="middle">
              {isConnectingAgent ? (
                <Text light weight="bold">
                  {t<TLocalesKeys>('header_title_connecting_agent')}
                </Text>
              ) : (
                <>
                  <Text light>
                    <Box>
                      <AIFilled />
                    </Box>
                  </Text>
                  <Text light weight="bold">
                    {t<TLocalesKeys>('widget_title_ai_chatbot')}
                  </Text>
                </>
              )}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box verticalAlign="middle" gap={2} flexShrink={1} overflow="hidden">
        {shouldShowAgentInfo ? (
          <Avatar
            size="size36"
            name={agent.fullName}
            imgProps={{ src: agent.profileImage }}
            dataHook={headerFullPageDataHooks.agentAvatar()}
          />
        ) : (
          <CaroIcon size={36} />
        )}
        <Box direction="vertical" overflow="hidden">
          <Text weight="bold" light dataHook={headerFullPageDataHooks.title()}>
            {t<TLocalesKeys>('subheader_title')}
          </Text>
          <Text
            size="small"
            light
            secondary
            ellipsis
            tooltipProps={titleTooltipProps}
            dataHook={headerFullPageDataHooks.secondaryTitle()}
          >
            {isLiveChat && agent ? agent.fullName : t<TLocalesKeys>('widget_title')}
          </Text>
        </Box>
      </Box>
    );
  }, [t, isLiveChat, agent, isAiChatbot, isConnectingAgent]);

  return (
    <>
      <Box className={style.contactAppHeader} align="space-between" verticalAlign="middle">
        {titleSection}
        <HeaderFullPageActions />
      </Box>
      <AnnouncementBanner dataHook={headerFullPageDataHooks.announcementBanner()} />
      <StatusBanner />
    </>
  );
};
